<template>
  <div v-if="visible" class="modal-backdrop" @click.self="close">
    <div class="modal">
      <div class="modal-hdr">
        <div class="modal-title">Private key</div>
        <button class="icon-btn tiny" type="button" @click="close" title="Close">✕</button>
      </div>

      <div class="modal-body">
        <div class="modal-sub">{{ shortKey(publicKey) }}</div>

        <textarea
          class="secret"
          readonly
          :value="privateKey"
          @focus="$event.target.select()"
        ></textarea>

        <div v-if="error" class="error">{{ error }}</div>

        <div class="modal-actions">
          <span v-if="hint" class="copy-hint" :class="hintType">{{ hint }}</span>

          <button class="primary-btn" type="button" :disabled="!privateKey" @click="copy">
            Copy
          </button>
          <button class="ghost-btn" type="button" @click="close">
            Close
          </button>
        </div>

        <p class="warn">
          Don’t store this in logs/screenshots. If the private key leaks, funds are gone.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { tryCopy } from '@/composables/clipboard'
import { shortKey } from '@/composables/format'

const props = defineProps({
  visible: { type: Boolean, default: false },
  publicKey: { type: String, default: '' },
  privateKey: { type: String, default: '' },
  error: { type: String, default: '' },
})

const emit = defineEmits(['close'])

const hint = ref('')
const hintType = ref('ok') // ok|bad
let timer = null

function setHint(text, type = 'ok') {
  hint.value = text
  hintType.value = type
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => { hint.value = '' }, 900)
}

async function copy() {
  const ok = await tryCopy(props.privateKey)
  setHint(ok ? 'Copied' : 'Copy failed', ok ? 'ok' : 'bad')
}

function close() {
  hint.value = ''
  if (timer) clearTimeout(timer)
  timer = null
  emit('close')
}

// авто-копирование при получении ключа (best effort)
watch(
  () => [props.visible, props.privateKey],
  async ([vis, pk]) => {
    if (!vis) return
    const t = String(pk || '').trim()
    if (!t) return
    const ok = await tryCopy(t)
    setHint(ok ? 'Copied' : 'Copy failed', ok ? 'ok' : 'bad')
  },
  { immediate: false }
)
</script>

<style scoped>
/* modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10020;
}

.modal {
  width: min(560px, calc(100vw - 32px));
  background: #0b0b0b;
  border: 1px solid #222;
  border-radius: 10px;
  overflow: hidden;
}

.modal-hdr {
  padding: 10px 12px;
  background: #1a1a1a;
  border-bottom: 1px solid #222;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.modal-title {
  font-weight: 600;
  font-size: 13px;
  color: #fff;
}

.modal-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.modal-sub {
  font-size: 12px;
  color: #9ca3af;
}

.secret {
  width: 100%;
  min-height: 110px;
  resize: vertical;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid #333;
  background: #050505;
  color: #e5e7eb;
  font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.modal-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
}

.warn {
  margin: 0;
  font-size: 11px;
  color: #6b7280;
}

.primary-btn,
.ghost-btn {
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid #333;
  font-size: 12px;
  cursor: pointer;
}

.primary-btn {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}
.primary-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.ghost-btn {
  background: transparent;
  color: #aaa;
}

.ghost-btn:hover:enabled {
  background: #1a1a1a;
}

.icon-btn {
  background: transparent;
  border: 1px solid #333;
  color: #aaa;
  border-radius: 6px;
  height: 28px;
  width: 28px;
  line-height: 26px;
  text-align: center;
  cursor: pointer;
  font-size: 11px;
  padding: 0;
}
.icon-btn.tiny {
  height: 20px;
  width: 20px;
  line-height: 18px;
  font-size: 10px;
}

.error {
  margin-top: 4px;
  padding: 4px 6px;
  border-radius: 4px;
  background: #2a1111;
  border: 1px solid #4a1f1f;
  color: #ffaaaa;
  font-size: 11px;
}

.copy-hint {
  margin-right: auto;
  font-size: 12px;
  user-select: none;
}
.copy-hint.ok { color: #22c55e; }
.copy-hint.bad { color: #f87171; }
</style>
